// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A robot's device ID in the archive page's header, filled in or corrected in place
 * (design doc §8.3). A robot may be filed before it has its number; until then the header
 * says so and offers 「补录设备编号」. The server checks the number as at filing — the
 * type's pattern, unique among archives — and records who set it in the audit log.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/endpoints";
import { usePermissions } from "@/hooks/usePermissions";
import { normalizeDeviceNo } from "@/scanner/serials";
import { ErrorBanner } from "./ErrorBanner";
import { IconButton } from "./IconButton";
import { CheckIcon, CloseIcon, EditIcon } from "./icons";

interface Props {
  archiveId: string;
  deviceNo: string | null;
}

export function DeviceNoEditor({ archiveId, deviceNo }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { canRecord } = usePermissions();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(deviceNo ?? "");

  const save = useMutation({
    mutationFn: () => api.archives.setDeviceNo(archiveId, normalizeDeviceNo(value)),
    onSuccess: async () => {
      setEditing(false);
      // The header, and every list that shows the number.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["archive"] }),
        queryClient.invalidateQueries({ queryKey: ["archives"] }),
      ]);
    },
  });

  if (!editing) {
    return (
      <span className="device-no">
        {deviceNo ? (
          <span className="mono muted">{deviceNo}</span>
        ) : (
          <span className="badge badge-warn">{t("archive.noDeviceNo")}</span>
        )}
        {canRecord && (
          <IconButton
            label={t(deviceNo ? "archive.editDeviceNo" : "archive.fillDeviceNo")}
            icon={<EditIcon />}
            onClick={() => {
              save.reset();
              setValue(deviceNo ?? "");
              setEditing(true);
            }}
          />
        )}
      </span>
    );
  }

  const blank = !normalizeDeviceNo(value);
  return (
    <form
      className="device-no device-no-editing"
      onSubmit={(event) => {
        event.preventDefault();
        if (!blank && !save.isPending) save.mutate();
      }}
    >
      <input
        aria-label={t("archive.device")}
        autoComplete="off"
        // Opened to type the number; focus belongs here.
        autoFocus
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setEditing(false);
        }}
      />
      <button
        type="submit"
        className="icon-button tooltip-trigger"
        aria-label={t("common.save")}
        data-tooltip={t("common.save")}
        disabled={blank || save.isPending}
      >
        <CheckIcon />
      </button>
      <button
        type="button"
        className="icon-button tooltip-trigger"
        aria-label={t("common.cancel")}
        data-tooltip={t("common.cancel")}
        onClick={() => setEditing(false)}
      >
        <CloseIcon />
      </button>
      <ErrorBanner error={save.error} />
    </form>
  );
}
